import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, pipe, withLatestFrom, zip } from 'rxjs';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { distinctUntilChanged, map, mergeMap, tap } from 'rxjs/operators';
import { HireItem } from '../../../models';
import { HireItemAttachment } from '../../../models/HireItemAttachment';
import { DocketService } from '../../services/docket.service';
import { HireItemService } from '../../services/hire-item.service';

interface HireItemListState {
  docketId: number;
  hireItems: HireItem[];
  loading: boolean;
  uploading: boolean;
  attachments: { [key: number]: HireItemAttachment[] };
  conflicts: { [key: number]: number };
}

const defaultState = {
  docketId: null,
  hireItems: [],
  loading: false,
  attachments: {},
  uploading: false,
  conflicts: {}
};

@Injectable()
export class HireItemListStoreService extends ComponentStore<HireItemListState> {
  constructor(
    private hireItemService: HireItemService,
    private toastService: ToastrService,
    private docketService: DocketService
  ) {
    super(defaultState);
  }

  readonly setDocketId = this.updater((state, docketId: number) => ({
    ...state,
    loading: true,
    docketId
  }));

  readonly loading$ = this.select((state) => state.loading);
  readonly uploading$ = this.select((state) => state.uploading);
  readonly hireItems$ = this.select((state) =>
    state.hireItems.filter(({ deleted }) => deleted === 0)
  );
  // an observable that emits an empty value when the hire items change
  readonly hireItemsChanged$ = this.hireItems$.pipe(
    map((hireItems) => hireItems.length),
    distinctUntilChanged(),
    map(() => void 0)
  );

  readonly hireItemsWithExtras$ = this.select(
    ({ hireItems, attachments, conflicts }) => {
      return hireItems
        .filter(({ deleted }) => deleted === 0)
        .map((item) => ({
          ...item,
          attachments: attachments[item.id] ?? [],
          conflictCount: conflicts[item.id] ?? 0
        }));
    }
  );

  readonly setDocketAndLoad = this.effect<number>(
    pipe(
      tap(() => this.patchState({ loading: true })),
      tap((docketId) => this.setDocketId(docketId)),
      mergeMap((docketId) => this.hireItemService.getHireItems(docketId)),
      tapResponse(
        (hireItems: HireItem[]) => {
          this.patchState({
            hireItems,
            loading: false
          });
        },
        (error: any) => {
          this.toastService.error(
            error?.message ?? 'Error loading hire items',
            'Could not load hire items'
          );
        }
      ),
      tap((hireItems) => this.loadAttachments()),
      tap(() => this.loadConflicts())
      // tap(() => this.patchState({ loading: false }))
    )
  );

  readonly reloadHireItems = this.effect<void>(
    pipe(
      withLatestFrom(this.select((state) => state.docketId)),
      mergeMap(([_, docketId]) => this.hireItemService.getHireItems(docketId)),
      tapResponse(
        (hireItems: HireItem[]) => {
          this.patchState({
            hireItems,
            loading: false
          });
        },
        (error: any) => {
          this.handleErrorGettingAttachments(error);
        }
      ),
      tap(() => this.loadConflicts())
    )
  );

  readonly onHireCount$ = this.select(
    ({ hireItems }) =>
      hireItems.filter((item) => !item.off_hire && item.deleted === 0).length
  );
  readonly totalHireItemCount$ = this.select(
    ({ hireItems }) => hireItems.filter(({ deleted }) => deleted === 0).length
  );

  private readonly loadConflicts = this.effect<void>(
    pipe(
      withLatestFrom(this.select((state) => state.docketId)),
      mergeMap(([_, docketId]) => this.docketService.conflictCounts(docketId)),
      tapResponse(
        (conflicts: any) => {
          this.patchState({
            conflicts: conflicts.reduce(
              (acc, conflict) => ({
                ...acc,
                [conflict.id]: conflict.conflict_count
              }),
              {}
            )
          });
        },
        (error: any) => console.log('error loading conflict counts', error)
      )
    )
  );

  private handleErrorGettingAttachments(error: any) {
    this.toastService.error(
      error?.message ?? 'Error loading hire items',
      'Could not load hire items'
    );
  }

  readonly loadAttachments = this.effect<void>(
    pipe(
      withLatestFrom(this.hireItems$),
      mergeMap(([_, hireItems]) =>
        zip(
          ...hireItems.map((hireItem) => this.loadItemAttachments(hireItem))
        ).pipe(
          map((attachments) =>
            attachments.reduce(
              (acc, attachment) => ({
                ...acc,
                [attachment.hireItemId]: attachment.attachments
              }),
              {}
            )
          )
        )
      ),
      tapResponse(
        (attachments: { [hireItemId: number]: HireItemAttachment[] }) =>
          this.patchState({ attachments }),
        (error) => this.handleErrorGettingAttachments(error)
      )
    )
  );

  readonly loadAttachmentsForItem = this.effect<number>(
    pipe(
      withLatestFrom(
        this.select(({ docketId, attachments }) => ({ docketId, attachments }))
      ),
      mergeMap(([hireItemId, { docketId, attachments }]) =>
        this.hireItemService.getItemAttachments(docketId, hireItemId).pipe(
          tapResponse(
            (newAttachments) => {
              this.patchState({
                attachments: {
                  ...attachments,
                  [hireItemId]: newAttachments
                }
              });
            },
            (error) => this.handleErrorGettingAttachments(error)
          )
        )
      )
    )
  );

  readonly deleteHireItem = this.effect<number>(
    pipe(
      withLatestFrom(this.select(({ docketId }) => docketId)),
      mergeMap(([hireItemId, docketId]) =>
        this.hireItemService.delete(docketId, hireItemId)
      ),
      tap(() => this.reloadHireItems())
    )
  );

  private loadItemAttachments(hireItem: HireItem) {
    return this.hireItemService
      .getItemAttachments(hireItem.docket_id, hireItem.id)
      .pipe(map((attachments) => ({ hireItemId: hireItem.id, attachments })));
  }

  readonly deleteAttachment = this.effect<{ hireItemId: number; id: number }>(
    pipe(
      withLatestFrom(this.select(({ docketId }) => docketId)),
      mergeMap(([{ hireItemId, id }, docketId]) =>
        this.hireItemService.deleteAttachment(docketId, hireItemId, id).pipe(
          tapResponse(
            () => this.loadAttachmentsForItem(hireItemId),
            (error: any) =>
              this.toastService.error(
                error?.message ?? 'Error deleting attachment',
                'Could not delete attachment'
              )
          )
        )
      )
    )
  );

  readonly addAttachments = this.effect<{
    payload: Pick<
      HireItemAttachment,
      'stage' | 'description' | 'hire_item_id'
    > & { files: File[]; uuid: string };
    onSuccess: () => void;
  }>(
    pipe(
      tap(() => this.patchState({ uploading: true })),
      withLatestFrom(this.select(({ docketId }) => docketId)),
      mergeMap(([{ payload, onSuccess }, docketId]) => {
        return forkJoin(
          payload.files.map((file, index) => {
            return this.hireItemService
              .getAttachmentUploadUrl(
                docketId,
                payload.hire_item_id,
                file.name,
                payload.uuid,
                index
              )
              .pipe(
                mergeMap(({ url }) => {
                  // upload to s3
                  return fromPromise(
                    fetch(url, {
                      method: 'PUT',
                      body: file
                    })
                  );
                }),
                mergeMap(() => {
                  const { files, ...attachPayload } = payload;
                  // attach via medialibrary
                  return this.hireItemService.associateAttachment(
                    docketId,
                    payload.hire_item_id,
                    { ...attachPayload, filename: file.name, index }
                  );
                })
              );
          })
        ).pipe(
          tapResponse(
            () => {
              this.loadAttachmentsForItem(payload.hire_item_id);
              onSuccess();
            },
            (error: any) =>
              this.toastService.error(
                error?.message ?? 'Error adding attachment',
                'Could not add attachment'
              )
          ),
          tap(() => this.patchState({ uploading: false }))
        );
      })
    )
  );

  readonly download = this.effect<HireItemAttachment>(
    pipe(
      withLatestFrom(this.select(({ docketId }) => docketId)),
      mergeMap(([{ hire_item_id, id, file_name }, docketId]) =>
        this.hireItemService
          .downloadAttachment(docketId, hire_item_id, id)
          .pipe(
            tapResponse(
              (response: any) => {
                const link = document.createElement('a');
                link.href = response.url;
                link.download = file_name;
                link.target = '_blank';
                link.click();
              },
              (error: any) =>
                this.toastService.error(
                  error?.message ?? 'Error downloading attachment',
                  'Could not download attachment'
                )
            )
          )
      )
    )
  );
}
